import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Trophy,
  TrendingUp,
  Users,
  Target,
  Calendar,
  MapPin,
  Star,
} from "lucide-react";

export function AnalyzaTimu() {
  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge
            variant="outline"
            className="mb-4 px-4 py-1.5 bg-primary/5 text-primary border-primary/20 rounded-full text-xs font-black uppercase tracking-widest"
          >
            Analýza tímu
          </Badge>
          <h2 className="text-3xl font-black tracking-tighter text-foreground sm:text-4xl mb-4">
            Cesta na <span className="text-primary">ZOH 2026 Miláno</span>
          </h2>
          <p className="text-lg text-muted-foreground/80 max-w-3xl mx-auto leading-relaxed font-medium">
            Slovenská hokejová reprezentácia sa pripravuje na jednu z
            najdôležitejších udalostí v novodobej histórii slovenského hokeja.
            Pozrite si podrobnú analýzu cesty kvalifikácie, súčasného kádra a
            šancí na úspech v Miláne.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Kvalifikácia a cesta na ZOH */}
          <Card className="border-muted/20 shadow-sm bg-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Trophy className="h-5 w-5 text-primary" />
                Kvalifikácia a cesta na olympiádu
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-muted/20">
                <h4 className="font-bold mb-2">Kvalifikačný turnaj</h4>
                <p className="text-muted-foreground mb-3">
                  Slovensko si vybojovalo účasť na ZOH 2026 cez kvalifikačný
                  turnaj, kde sa prebojovalo proti silným európským tímom.
                  Klíčovým momentom bolo víťazstvo vo finálovom stretnutí, ktoré
                  zabezpečilo miestenku v Miláne.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Kvalifikácia: August 2025</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/30 border border-muted/20">
                <h4 className="font-bold mb-2">Skupina B v Miláne</h4>
                <p className="text-muted-foreground mb-3">
                  V základnej skupine B sa Slovensko stretne s tradičnými
                  hokejovými veľmocami. Program zápasov je náročný, ale každý
                  zápas prináša šancu na prekvapenie.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Miláno-Cortina, Taliansko</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Súčasný káder a lídri */}
          <Card className="border-muted/20 shadow-sm bg-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Users className="h-5 w-5 text-primary" />
                Súčasný káder a kľúčoví hráči
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-muted/20">
                <h4 className="font-bold mb-2">NHL hviezdy</h4>
                <p className="text-muted-foreground mb-3">
                  Základom tímu sú hráči z NHL, ktorí prinášajú skúsenosti z
                  najlepšej hokejovej ligy sveta.
                  <strong>Juraj Slafkovský</strong> (Montreal) a{" "}
                  <strong>Šimon Nemec</strong> (New Jersey) sú budúcnosť
                  slovenského hokeja a už teraz patria medzi kľúčové postavy.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="h-4 w-4" />
                  <span>15+ hráčov v NHL</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/30 border border-muted/20">
                <h4 className="font-bold mb-2">Európska legenda</h4>
                <p className="text-muted-foreground mb-3">
                  Skúsenosti z európských líg dopĺňajú káder o hráčov, ktorí sú
                  zvyknutí na medzinárodný štýl hry. Ich prínos je neoceniteľný,
                  najmä v zápasoch proti európskym tímom.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Target className="h-4 w-4" />
                  <span>KHL, SHL, česká extraliga</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Šance na úspech a prognóza */}
        <Card className="border-muted/20 shadow-sm bg-transparent mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <TrendingUp className="h-5 w-5 text-primary" />
              Šance na úspech a prognóza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg bg-muted/30 border border-muted/20 text-center">
                <div className="text-3xl font-bold text-primary mb-2">25%</div>
                <div className="font-semibold mb-2">Šanca na medailu</div>
                <p className="text-sm text-muted-foreground">
                  S kvalitným kádrom a trochou šťastia môže Slovensko bojovať o
                  medailové pozície.
                </p>
              </div>

              <div className="p-6 rounded-lg bg-muted/30 border border-muted/20 text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  60%
                </div>
                <div className="font-semibold mb-2">Postup do štvrťfinále</div>
                <p className="text-sm text-muted-foreground">
                  Realistický cieľ je postup z základnej skupiny a účasť vo
                  vyraďovacích bojoch.
                </p>
              </div>

              <div className="p-6 rounded-lg bg-muted/30 border border-muted/20 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">85%</div>
                <div className="font-semibold mb-2">Zápasová forma</div>
                <p className="text-sm text-muted-foreground">
                  Tím má potenciál dosiahnuť dobrú formu včas na hlavný turnaj.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Taktický prístup */}
        <Card className="border-muted/20 shadow-sm bg-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Target className="h-5 w-5 text-primary" />
              Taktický prístup a herný štýl
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold mb-2">Bránenie</h4>
                  <p className="text-muted-foreground">
                    Kľúčom k úspechu bude stabilná bránniarecká dvojica. V NHL
                    overení brankári budú musieť podávať vyrovnané výkony a
                    chytit kľúčové strely v kritických momentoch zápasu.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">Obrana</h4>
                  <p className="text-muted-foreground">
                    Mobilná a fyzicky silná obrana, ktorá dokáže efektívne
                    krýtiť protiútoky a rýchlo prepínať do útoku, bude základom
                    herného systému.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-bold mb-2">Útok</h4>
                  <p className="text-muted-foreground">
                    Rýchly prechodový hokej s dôrazom na presilovkové hry a
                    využívanie rýchlosti krídelných útočníkov by mal byť hlavnou
                    zbraňou tímu.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">Špeciálne tímy</h4>
                  <p className="text-muted-foreground">
                    Efektívne presilovky a oslabenia budú rozhodujúce v tesných
                    zápasoch proti favorizovaným súperom.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground italic">
            Táto analýza poskytuje komplexný pohľad na slovenskú reprezentáciu
            pred ZOH 2026. Každý zápas je príležitosťou prekročiť očakávania a
            napísať novú kapitolu slovenského hokeja.
          </p>
        </div>
      </div>
    </section>
  );
}
